/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      // Honor the initial selection rendered by Liquid, including an explicit variant URL.
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.updateStorageTitle();
        this.syncStickyPrice();
        this.updateRefurbishedBadge();
      });
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (!evt.target.closest('.option-selector')) return;
      this.normalizeAvailableSelections();
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.syncStickyPrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);
      this.updateNormalizedColorLabels();
      this.updateStorageTitle();

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]:not(.reacondicionado-badge)').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
      this.updateRefurbishedBadge();
    }

    /** Updates only the refurbished badges belonging to this product picker. */
    updateRefurbishedBadge() {
      const selectedVariantId = this.variant ? String(this.variant.id) : null;
      this.section.querySelectorAll('.reacondicionado-badge[data-variant-metafield]').forEach((badge) => {
        badge.classList.toggle(
          'hidden',
          !selectedVariantId || badge.dataset.variantMetafield !== selectedVariantId
        );
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }

      this.syncStickyPurchaseState();
    }

    /** Keep the lower purchase button aligned with the main button. */
    syncStickyPurchaseState() {
      if (!this.addBtn) return;
      const productSection = this.section.closest('.cc-main-product') || this.section;
      productSection.querySelectorAll(
        '.bottom-modal-container [data-sticky-add-to-cart], .bottom-modal-container #submit-from-modal'
      ).forEach((button) => {
        button.disabled = this.addBtn.disabled;
        button.setAttribute('aria-disabled', String(this.addBtn.disabled));
        button.textContent = this.addBtn.textContent;
      });
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      const hideUnavailable = this.shouldHideUnavailable();
      if (this.dataset.showAvailability === 'false' && !hideUnavailable) return;
      const availabilityMethod = hideUnavailable ? 'downward' : this.dataset.availabilityMethod;
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');

          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown'
              ? option.dataset.value
              : option.value;

            let variantsExist = false;
            let variantsAvailable = false;

            this.data.product.variants.forEach((v) => {
              let matches = 0;

              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }

              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;

                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });

            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
            if (hideUnavailable) {
              // Hide unavailable values only in Color and Storage (first two options).
              // Keep condition values visible and marked unavailable.
              const hideOption = selectorIndex < 2 && !variantsAvailable;
              option.removeAttribute('data-initial-unavailable');
              // Condition options remain clickable to compare existing variant prices.
              option.disabled = selectorIndex < 2 && !variantsAvailable;
              const label = option.nextElementSibling;
              if (label && label.matches('label')) {
                label.hidden = hideOption;
                if (hideOption) label.style.setProperty('display', 'none', 'important');
                else label.style.removeProperty('display');
              }
            }
          });
        });
      }
    }

    /** Hide unavailable options in button and image selectors. */
    shouldHideUnavailable() {
      return Array.from(this.optionSelectors).every((selector) => selector.dataset.selectorType === 'listed');
    }

    normalizeAvailableSelections() {
      if (!this.shouldHideUnavailable()) return;
      const selected = this.getSelectedOptions();
      this.optionSelectors.forEach((selector, index) => {
        // Preserve the condition chosen by the customer, even when sold out.
        // Only normalize Color and Storage to available values.
        if (index >= 2 || selected[index] === null) return;
        const candidates = this.data.product.variants.filter((variant) => variant.available
          && variant.options.slice(0, index).every((value, previousIndex) =>
            selected[previousIndex] === null || value === selected[previousIndex]));
        if (!candidates.length || candidates.some((variant) => variant.options[index] === selected[index])) return;
        const inputs = Array.from(selector.querySelectorAll('input.js-option'));
        const next = inputs.find((input) => candidates.some((variant) => variant.options[index] === input.value));
        if (!next) return;
        inputs.forEach((input) => { input.checked = input === next; });
        next.disabled = false;
        selected[index] = next.value;
      });
    }

    updateNormalizedColorLabels() {
      if (!this.shouldHideUnavailable()) return;
      this.optionSelectors.forEach((selector) => {
        const text = selector.querySelector('.js-color-text');
        const selected = selector.querySelector('input:checked');
        if (text) text.textContent = selected ? selected.value : '';
      });
    }

    /**
     * Sincroniza los precios inferiores con el precio principal.
     */
    syncStickyPrice() {
      const source = this.section.querySelector('.product-info__price > .price');
      if (!source) return;

      const productSection = this.section.closest('.cc-main-product') || this.section;

      productSection.querySelectorAll(
        'sticky-atc-panel .price, .bottom-modal-container .product-pricing .price'
      ).forEach((target) => {
        if (target === source) return;

        target.innerHTML = source.innerHTML;

        ['price--on-sale', 'price--sold-out'].forEach((className) => {
          target.classList.toggle(
            className,
            source.classList.contains(className)
          );
        });
      });
    }

    /**
     * Añade el almacenamiento seleccionado a los títulos marcados.
     */
    updateStorageTitle() {
      const optionIndex = this.data.product.options.findIndex((option) => {
        const name = typeof option === 'string' ? option : option.name;

        return ['almacenamiento', 'storage'].includes(
          String(name).trim().toLowerCase()
        );
      });

      if (optionIndex < 0) return;

      const storage = this.getSelectedOptions()[optionIndex];

      document.querySelectorAll('[data-storage-title]').forEach((title) => {
        if (
          title.dataset.storageProduct !== String(this.data.product.id)
          || title.dataset.storageSection !== this.section.dataset.section
        ) return;

        const base = title.dataset.baseTitle;
        title.textContent = storage ? `${base} ${storage}` : base;
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );

        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');

      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let { variant } = this;

      if (this.preSelection) {
        variant = this.data.product.variants[0];

        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price) {
            variant = this.data.product.variants[i];
          }
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;

        if (priceWasEl) {
          priceWasEl.innerHTML = this.data.formatted[this.variant.id].compareAtPrice || '';
        }

        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;

        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );

        this.price.classList.toggle(
          'price--sold-out',
          !this.variant.available && !this.preSelection
        );
      }

      this.price.querySelector('.price__default').hidden = !this.variant && !this.preSelection;
      this.price.querySelector('.price__no-variant').hidden = this.variant || this.preSelection;

      const from = this.price.querySelector('.price__from');

      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;

      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id].weight
          : '';

        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;

      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;

      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}?variant=${this.variant.id}`
      );
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();

          selectedOptions.push(
            selectedText === this.dataset.placeholderText ? null : selectedText
          );
        } else {
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();

      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}